import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PaymentFormRule, PaymentFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPayment } from "@factory/form/form-default-payment.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePaymentModel } from "@factory/model/query-model-payment";
import { 
  useQyDeletePayment, 
  useQyGetPaymentById, 
  useQyUpdatePayment} from "@factory/query/payment.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditPayment() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPayment();
  const navigate = useNavigate();
  const { mapUpdatePayment, mapDeletePayment } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { paymentId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Payment
  const handleGetApiSuccess = (data: GetResponsePaymentModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("voucher", responseData?.voucher || "");
      setValue("description", responseData?.description || "");
      setValue("paymentType", responseData?.payment_type || "");
      setValue("paymentDate", formatDate(responseData?.payment_date) as any, undefined);
      setValue("amount", responseData?.amount);
  };
  const {
    data: paymentResponse,
    isLoading,
    isError: paymentError,
  } = useQyGetPaymentById(paymentId || "", handleGetApiSuccess);
  const paymentData = paymentResponse?.data?.[0];

  // API UPDATE Payment
  const handleUpdateApiSuccess = () => {
    showSuccess("Payment updated successfully");
  };
  const { mutate: updatePayment, isLoading: isEditLoading } = useQyUpdatePayment(
    handleUpdateApiSuccess
  );

  // API DELETE Payment
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Payment deleted successfully");
  };
  const { mutate: deletePayment } = useQyDeletePayment(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PaymentFormSchema>({
    defaultValues: getDefault(paymentData),
    resolver: zodResolver(PaymentFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PaymentFormSchema) => {
    if (!paymentData || !paymentData.code) {
      throw new Error("No payment data");
    }

    const formData = mapUpdatePayment(form, paymentData.code);
    updatePayment(formData);
  };
  const handleValidateError = (err: FieldErrors<PaymentFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!paymentData || !paymentData.code) {
      throw new Error("No payment data found");
    }

    const form = mapDeletePayment(paymentData.code);
    deletePayment(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    paymentId,
    paymentData,
    isLoading,
    paymentError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}